import { routes } from '@/constants/routes.const';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import FlexWrap from '@/components/common/FlexWrap';
import TextButton from '@/components/common/TextButton';
import UploadReceiptImage from '@/components/Home/UploadReceiptImage';
import BtnWithIcon from '@/components/Receipt/BtnWithIcon';

import { HOME_BOTTOM_SECTION, HOME_TOP_SECTION } from '@/constants/home.const';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState, initialGlobalState } from '@/store';
import ReceiptAccordionContent from './ReceiptAccordionContent';

type Props = {
  refetchHome: () => void;
  scrollToReceipt?: boolean;
};
function BottomSection({ refetchHome, scrollToReceipt }: Props) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {
    isRefundInfoRegistered,
    nationality,
    haveSeenReceipt,
    isReceiptImageUploaded,
    isUploadRequired,
    unapprovedCount,
    isCashPickup,
    passportNumber,
  } = useAppSelector((state) => state) as IGlobalState;
  const customerNationToken = localStorage.getItem('customerNation');
  const currNation = isMobile ? customerNationToken : nationality;
  const isCruise =
    (passportNumber && localStorage.getItem(passportNumber) === 'Cruise') ||
    false;

  const [isReceiptToggleActive, setIsReceiptToggleActive] = useState(false);
  const [isUploadToggleActive, setIsUploadToggleActive] = useState(false);

  const onClickRefundInfo = () => {
    if (isRefundInfoRegistered) {
      history.push(routes.refundInfo);
    } else {
      history.push(routes.customerRefund);
    }
  };

  const onClickReceiptInquiry = () => {
    dispatch({ type: 'UPDATE_USER_INFO', payload: { refundAfter: true } });
    history.push(routes.airportPrintReceipt);
  };

  const onOpenLogoutModal = () => {
    const logoutModalContent =
      HOME_TOP_SECTION.getLogoutModalContent(nationality);

    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        isActive: true,
        type: 'CUSTOM',
        data: {
          ...logoutModalContent,
          btnCallback: logout,
        },
      },
    });
  };

  const logout = () => {
    dispatch({
      type: 'UPDATE_USER_INFO',
      payload: initialGlobalState,
    });
    const isReadAirportGuide = localStorage.getItem('isReadRefundProcedure');
    localStorage.clear();
    if (isReadAirportGuide) {
      localStorage.setItem('isReadRefundProcedure', isReadAirportGuide);
    }
    history.replace(routes.passport);
  };

  const goToRefundProcedure = () => {
    if (isCashPickup) {
      history.push(routes.cashPickupIntro + '?from=home');
      return;
    }
    history.push(routes.refundProcedure);
  };

  useEffect(() => {
    if (!scrollToReceipt) {
      return;
    }
    window.scrollTo({ top: 700, behavior: 'smooth' });
    setTimeout(() => {
      setIsReceiptToggleActive(true);
    }, 700);
  }, []);

  return (
    <Container>
      <MainContent>
        <Title>{HOME_BOTTOM_SECTION.getTitle(nationality)}</Title>
        <RefundProcedure dir='column'>
          <BtnWithIcon
            title={HOME_BOTTOM_SECTION.refundInfoButton.getTitle({
              currNation,
              isCashPickup,
            })}
            description={HOME_BOTTOM_SECTION.refundInfoButton.getDescription({
              currNation,
              nationality,
              isUploadRequired,
              isRefundInfoRegistered,
              isCashPickup,
            })}
            isCompleted={isRefundInfoRegistered}
            onClick={onClickRefundInfo}
            margin='0 0 28px'
          />
          <BtnWithIcon
            title={HOME_BOTTOM_SECTION.receiptInquiryButton.getTitle({
              currNation,
              isUploadRequired,
            })}
            description={HOME_BOTTOM_SECTION.receiptInquiryButton.getDescription(
              {
                currNation,
                isUploadRequired,
                isReceiptImageUploaded,
                unapprovedCount,
                nationality,
              },
            )}
            isCompleted={haveSeenReceipt}
            onClick={onClickReceiptInquiry}
            margin='0 0 28px'
            isToggle={!isUploadRequired}
            subContentComponent={
              isUploadRequired ? undefined : (
                <ReceiptAccordionContent
                  style={{
                    paddingTop: 20,
                    marginTop: 18,
                    borderTop: '1px solid #CBCCCE',
                  }}
                />
              )
            }
            isToggleActive={isReceiptToggleActive}
            setIsToggleActive={setIsReceiptToggleActive}
          />
          {isUploadRequired && (
            <BtnWithIcon
              title={HOME_BOTTOM_SECTION.uploadReceiptButton.getTitle(
                nationality,
                isCruise,
              )}
              description={HOME_BOTTOM_SECTION.uploadReceiptButton.getDescription(
                isReceiptImageUploaded,
                nationality,
                isCruise,
              )}
              isCompleted={isReceiptImageUploaded}
              margin='0 0 28px'
              isToggle
              subContentComponent={
                <UploadReceiptImage refetchHome={refetchHome} />
              }
              isToggleActive={isUploadToggleActive}
              setIsToggleActive={setIsUploadToggleActive}
            />
          )}
        </RefundProcedure>
      </MainContent>
      <BtnContainer>
        <TextButton
          title={HOME_BOTTOM_SECTION.footerButtons.getProcedureGuideText(
            nationality,
          )}
          onClick={goToRefundProcedure}
          color='#1856CD'
          bgColor='#fff'
          outline
          margin='0 0 12px'
        />
        <TextButton
          title={HOME_BOTTOM_SECTION.footerButtons.getLogoutText(nationality)}
          onClick={onOpenLogoutModal}
          color='#1856CD'
          bgColor='#fff'
          outline
        />
      </BtnContainer>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  overflow-y: auto;
`;
const MainContent = styled.div`
  background-color: #ffffff;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  width: 100%;
  -webkit-box-shadow: 0px 20px 20px -20px rgba(174, 191, 204, 0.5);
  box-shadow: 0px -12px 16px -2px rgba(174, 191, 204, 0.5);
  padding: 36px 20px 40px;
  margin-top: 20px;
  border-bottom: 8px solid #f5f6f7;
`;
const Title = styled.h3`
  font-size: 18px;
  line-height: 150%;
  font-weight: 700;
  color: #3a3b3e;
`;
const RefundProcedure = styled(FlexWrap)`
  width: 100%;
  margin-top: 20px;
`;
const BtnContainer = styled.div`
  padding: 20px;
  background-color: #fff;
`;
export default BottomSection;
